import * as React from 'react';
import { useIntl } from 'react-intl';
import {
  CmdAlert,
  CmdInput,
  CmdModal,
  CmdModalFooterButton,
} from '@commander-services/gui-components';
import { Controller, FieldError, useForm } from 'react-hook-form';
import { useQueries } from '@tanstack/react-query';
import FormikValidatorService, {
  IValidationApiResponse,
} from '../../Services/FormikValidatorService';
import { IRadioInputValue } from '../Forms/Fields/RadioInput';
import { ICheckboxGroupOption } from '../Forms/Fields/CheckboxGroup';
import { IGroupChipSelectOptions } from '../Forms/Fields/interfaces';
import { IGroupSelectOptions, IOption } from '../Forms/CmdField';
import { IChipInputItem } from '../Forms/Fields/ChipInput';
import {
  ILabelInputs,
  IRuleFormValues,
  ISelectedChips,
  RadioBtnTooltipsKeys,
  RuleFormValue,
  TRuleForm,
} from './types';
import GroupSelectHookForm from '../Forms/Fields/GroupSelectHookForm';
import GroupChipSelectHookForm from '../Forms/Fields/GroupChipSelectHookForm';
import { RadioInputHookForm } from '../Forms/Fields/RadioInputHookForm';
import {
  radioBtnTooltipsTranslationKeys,
  rangeName,
  RULE_TYPE_BINARY,
  RULE_TYPE_BINARY_GROUP,
  RULE_TYPE_RANGE,
  RULE_TYPE_WAYPOINTS,
} from './constants';
import CheckboxGroupHookForm from '../Forms/Fields/CheckboxGroupHookForm';
import CheckboxInputHookForm from '../Forms/Fields/CheckboxInputHookForm';
import { transformErrors, validateFields } from '../../Services/HookFormService';
import {
  getData,
  getLabels,
  getRules,
  getValidation,
  getValidationAll,
  isCheckboxCondition,
  isCheckboxGroupCondition,
  isRangeCondition,
  isWaypointCondition,
} from './AlarmRuleService';
import useAnalytics from '../../hooks/useAnalytics';

interface IRuleFormProps {
  rideOwnerShipValue?: string;
  ridePublicityValue?: string;
  selectedChips?: ISelectedChips[];
  closeCallback: () => void;
  responseCallback: (
    values: IChipInputItem,
    valuesToSent: { type: keyof IRuleFormValues; value: RuleFormValue }
  ) => void;
  openCallback: () => void;
}

export default function RuleForm(props: IRuleFormProps) {
  const { formatMessage: f } = useIntl();
  const [isLoaderActive, setIsLoaderActive] = React.useState<boolean>(false);
  const [dataOptions, setDataOptions] = React.useState<
    IGroupChipSelectOptions | IRadioInputValue[] | ICheckboxGroupOption[] | null
  >(null);
  const [conditionSelectOption, setConditionSelectOption] = React.useState<IGroupSelectOptions>({});
  const [validation, setValidation] = React.useState<IValidationApiResponse | null>(null);
  const [fieldName, setFieldName] = React.useState<string>('');
  const [labelInputs, setLabelInputs] = React.useState<ILabelInputs>({
    indexIdMin: 'alarms.modal.rule.min.text.default',
    indexIdMax: 'alarms.modal.rule.max.text.default',
    customLabel: 'alarms.modal.rule.custom.default',
    placeholder: 'alarms.modal.rule.placeholder.default',
    unit: '',
    type: 'range',
  });
  const [isNextForm, setIsNextForm] = React.useState<boolean>(false);
  const [specificValidation, setSpecificValidation] = React.useState<IValidationApiResponse>();
  const [isAlert, setIsAlert] = React.useState<boolean>(false);
  const [isEmptyOptions, setIsEmptyOptions] = React.useState<boolean>(false);
  const [isReset, setIsReset] = React.useState<boolean>(false);
  const { trackEvent } = useAnalytics();

  const {
    handleSubmit,
    control,
    setValue,
    getValues,
    trigger,
    watch,
    reset,
    formState: { errors },
  } = useForm<IRuleFormValues>({
    mode: 'onChange',
    defaultValues: {
      condition: '',
      waypoint_entered: undefined,
      waypoint_left: undefined,
      waypoint_in: undefined,
      waypoint_out: undefined,
      bin_2: '1',
      bin_3: '1',
      analog_1: '1',
      analog_2: '1',
      ignition: '1',
      doors: [],
      movement: '1',
      ride_privacy: 'private',
      engine_rpm: '',
      vehicle_speed: '',
      engine_temperature: '',
      fuel_level_liters: '',
      fuel_level_perc: '',
      battery_voltage: '',
      throttle_position: '',
      tachometer: '',
      satellites_count: '',
      altitude: '',
      eco_drive: '',
      temp1: undefined,
      temp2: undefined,
      min: '',
      max: '',
      ev_charging_start: '1',
      ev_plugged_in: '1',
      ev_battery_level: '',
      ev_range: '',
      esp_off: '1',
      driver_seatbelt: '1',
      jamming: '0',
      factory_emulated_alarm: '1',
      car_remote_lock: '1',
      auto_geofence: '1',
      unplug: '0',
      coolant_level_low: '',
      oil_level_low: '',
      check_engine: '',
    },
    resolver: async (formData) => {
      if (!validation) {
        return { values: formData, errors: {} };
      }
      const name = formData.condition;
      let rangeErros = {};

      if (specificValidation) {
        specificValidation.condition = [
          {
            param: 'required',
            value: true,
            message: f({ id: 'alarms.modal.rule.validation.required' }),
          },
        ];
        if (rangeName.includes(name)) {
          // const minValue = validation[name][0].value;
          const maxValue = validation[name][1].value;

          if (formData.min.length > 0 && formData.max.length > 0) {
            specificValidation.min = [
              validation[name][0],
              validation[name][1],
              {
                param: 'required',
                value: true,
                message: f({ id: 'alarms.modal.rule.validation.min.one.input' }),
              },
            ];
            specificValidation.max = [
              validation[name][0],
              validation[name][1],
              {
                param: 'required',
                value: true,
                message: f({ id: 'alarms.modal.rule.validation.min.one.input' }),
              },
            ];
            if (Number(formData.min) >= Number(formData.max)) {
              specificValidation.min = [
                validation[name][0],
                {
                  param: 'max',
                  value: Number(formData.max),
                  message: f({ id: 'alarms.modal.rule.validation.max.values' }),
                },
                validation[name][2],
              ];
            } else {
              specificValidation.min = [
                validation[name][0],
                {
                  param: 'max',
                  value: maxValue,
                  message: f({ id: 'alarms.modal.rule.validation.max.values' }),
                },
                validation[name][2],
              ];
            }
          } else if (formData.min.length > 0) {
            specificValidation.min = [
              validation[name][0],
              validation[name][1],
              validation[name][2],
            ];
            specificValidation.max = [validation[name][0], validation[name][1]];
          } else if (formData.max.length > 0) {
            specificValidation.max = [
              validation[name][0],
              validation[name][1],
              validation[name][2],
            ];
            specificValidation.min = [validation[name][0], validation[name][1]];
          } else {
            specificValidation.min = [
              validation[name][0],
              validation[name][1],
              {
                param: 'required',
                value: true,
                message: f({ id: 'alarms.modal.rule.validation.min.one.input' }),
              },
            ];
            specificValidation.max = [
              validation[name][0],
              validation[name][1],
              {
                param: 'required',
                value: true,
                message: f({ id: 'alarms.modal.rule.validation.min.one.input' }),
              },
            ];
          }

          specificValidation[name] = [];
        }

        rangeErros = FormikValidatorService.validateByValidationApiResponse(
          formData,
          specificValidation
        );
      }

      const formValidation = {
        ...validation,
        ...specificValidation,
        condition: [
          {
            param: 'required',
            value: true,
            message: f({ id: 'form.required' }),
          },
        ],
      };
      const validateErros = validateFields(formData, formValidation);

      let formErrors: Record<string, FieldError> = transformErrors(validateErros);

      if (formData.condition) {
        formErrors = Object.keys(formErrors)
          .filter((key) => key === formData.condition)
          .reduce(
            (obj, key) => {
              obj[key] = formErrors[key];
              return obj;
            },
            {} as Record<string, FieldError>
          );
      }

      if (isRangeCondition(formData.condition)) {
        formErrors = transformErrors(rangeErros);
      }

      return {
        values: formErrors && Object.keys(formErrors).length ? {} : formData, // if there's an error, return empty values
        errors: formErrors,
      };
    },
  });

  const convertDataForAlarmForm = (
    labelInputsType: ILabelInputs,
    dataOptionsForLabel:
      | IGroupChipSelectOptions
      | IRadioInputValue[]
      | ICheckboxGroupOption[]
      | null,
    valuesForLabels: IRuleFormValues
  ): {
    valuesForChipInput: { id: keyof IRuleFormValues; label: string };
    valuesForSent: { type: keyof IRuleFormValues; value: RuleFormValue };
  } => {
    const typeName = valuesForLabels.condition as keyof IRuleFormValues;

    let valuesForChipInput: { id: keyof IRuleFormValues; label: string } = {
      id: typeName,
      label: f({ id: labelInputsType.indexIdMin }),
    };

    let valuesForSent: { type: keyof IRuleFormValues; value: RuleFormValue } = {
      type: typeName,
      value: valuesForLabels[typeName] ?? '',
    };

    if (
      labelInputsType.type === RULE_TYPE_WAYPOINTS &&
      dataOptionsForLabel &&
      typeof valuesForLabels[typeName] === 'object' &&
      valuesForLabels[typeName] !== null
    ) {
      const waypointValues = valuesForLabels[typeName] as Record<string, string[]>;

      const selectedChips = Object.keys(waypointValues ?? {})[0];

      if (
        selectedChips &&
        Array.isArray(waypointValues[selectedChips]) &&
        waypointValues[selectedChips].length > 3
      ) {
        valuesForChipInput = {
          id: typeName,
          label: `${f({ id: labelInputsType.indexIdMin })}: ${f({
            id: 'alarm.modal.rule.waypoints.name',
          })} ${waypointValues[selectedChips].length}`,
        };
      } else {
        const firstFiltered =
          (dataOptionsForLabel as IGroupChipSelectOptions)?.[
            Object.keys(waypointValues ?? {})[0]
          ]?.filter((item) => waypointValues[selectedChips]?.includes(String(item.value))) ?? [];

        const secondFiltered =
          (dataOptionsForLabel as IGroupChipSelectOptions)?.[
            Object.keys(waypointValues ?? {})[1]
          ]?.filter(
            (item) =>
              waypointValues[selectedChips]?.includes(String(item.value)) &&
              !firstFiltered.find((firstItem) => String(firstItem.value) === String(item.value))
          ) ?? [];

        let formattedWaypoints = '';

        if (firstFiltered.length > 0) {
          firstFiltered.forEach((waypoint, index) => {
            formattedWaypoints += index === 0 ? ` ${waypoint.item}` : `, ${waypoint.item}`;
          });
          secondFiltered.forEach((waypoint) => {
            formattedWaypoints += `, ${waypoint.item}`;
          });
        } else {
          secondFiltered.forEach((waypoint, index) => {
            formattedWaypoints += index === 0 ? ` ${waypoint.item}` : `, ${waypoint.item}`;
          });
        }

        valuesForChipInput = {
          id: typeName,
          label: `${f({ id: labelInputsType.indexIdMin })}: ${formattedWaypoints}`,
        };
      }

      valuesForSent = {
        type: typeName,
        value: [...(waypointValues[selectedChips] ?? [])],
      };
    } else if (
      labelInputsType.type === RULE_TYPE_BINARY &&
      dataOptionsForLabel &&
      valuesForLabels[typeName] !== undefined &&
      valuesForLabels[typeName] !== null
    ) {
      const selectedOption = (dataOptionsForLabel as IRadioInputValue[]).find(
        (item) => String(item.value) === String(valuesForLabels[typeName])
      );

      valuesForChipInput = {
        id: typeName,
        label: selectedOption
          ? `${f({ id: labelInputsType.indexIdMin })}: ${selectedOption.item.toLowerCase()}`
          : `${f({ id: labelInputsType.indexIdMin })}: ${String(valuesForLabels[typeName])}`,
      };

      valuesForSent = {
        type: typeName,
        value: valuesForLabels[typeName],
      };
    } else if (labelInputsType.type === RULE_TYPE_RANGE) {
      const min = valuesForLabels.min ?? '';
      const max = valuesForLabels.max ?? '';

      if (min && max) {
        valuesForChipInput = {
          id: typeName,
          label: `${f({ id: labelInputsType.customLabel })}: ${min} - ${max} ${labelInputsType.unit}`,
        };
      } else if (min) {
        valuesForChipInput = {
          id: typeName,
          label: `${f({ id: labelInputsType.customLabel })}: ${f({
            id: 'alarms.modal.rule.label.from',
          })} ${min} ${labelInputsType.unit}`,
        };
      } else if (max) {
        valuesForChipInput = {
          id: typeName,
          label: `${f({ id: labelInputsType.customLabel })}: ${f({
            id: 'alarms.modal.rule.label.to',
          })} ${max} ${labelInputsType.unit}`,
        };
      } else {
        valuesForChipInput = {
          id: typeName,
          label: f({ id: labelInputsType.customLabel }),
        };
      }

      valuesForSent = {
        type: typeName,
        value: { min, max },
      };
    } else if (
      labelInputsType.type === RULE_TYPE_BINARY_GROUP &&
      dataOptionsForLabel &&
      Array.isArray(valuesForLabels[typeName]) &&
      valuesForLabels[typeName].length > 0
    ) {
      const binaryValues = valuesForLabels[typeName] as string[];

      const selectedItems = (dataOptionsForLabel as ICheckboxGroupOption[])
        .filter((item) => binaryValues.includes(String(item.value)))
        .map((item) => item.item)
        .join(', ');

      valuesForChipInput = {
        id: typeName,
        label: selectedItems
          ? `${f({ id: labelInputsType.indexIdMin })}: ${selectedItems}`
          : f({ id: labelInputsType.indexIdMin }),
      };

      valuesForSent = {
        type: typeName,
        value: binaryValues,
      };
    } else {
      valuesForChipInput = {
        id: typeName,
        label: f({ id: labelInputsType.indexIdMin }),
      };
    }

    return { valuesForChipInput, valuesForSent };
  };

  const getAlarmRules = React.useCallback(async () => {
    setIsLoaderActive(true);
    const data = await getRules(props.rideOwnerShipValue, props.ridePublicityValue);

    if (data) {
      const filteredData: IGroupSelectOptions = Object.assign(data);

      const tempData: IGroupSelectOptions = {};

      Object.keys(filteredData).forEach((items: string, index: number) => {
        tempData[items] = Object.values(filteredData)[index].filter(
          (values: IOption) =>
            values.passed === true &&
            props.selectedChips &&
            !props.selectedChips.find((item: ISelectedChips) => item.id === values.value && item.id)
        );

        if (tempData[items].length === 0) {
          delete tempData[items];
        }
      });

      if (Object.keys(tempData).length === 0) {
        setIsAlert(true);
        setIsEmptyOptions(true);
      } else {
        setIsEmptyOptions(false);
        setConditionSelectOption(tempData);
      }
    }
    setIsLoaderActive(false);
  }, [props.rideOwnerShipValue, props.ridePublicityValue, props.selectedChips]);

  const handleOnSubmit = (values: IRuleFormValues) => {
    convertDataForAlarmForm(labelInputs, dataOptions, values);

    props.responseCallback(
      convertDataForAlarmForm(labelInputs, dataOptions, values).valuesForChipInput,
      convertDataForAlarmForm(labelInputs, dataOptions, values).valuesForSent
    );

    if (isNextForm) {
      reset();
      setIsReset(true);
      props.openCallback();
      setDataOptions(null);
    } else {
      props.closeCallback();
    }
  };

  const buttons: CmdModalFooterButton[] = [
    {
      id: 'close-modal',
      type: 'button',
      className: 'e-button',
      title: f({ id: 'alarms.modal.rule.button.close' }),
      tooltip: f({ id: 'alarms.modal.rule.button.close.tooltip' }),
      closeCallback: props.closeCallback,
    },
    {
      id: 'rule-form-submit',
      type: 'submit',
      className: 'e-button e-button--gray',
      title: f({ id: 'alarms.modal.rule.button.addrule' }),
      tooltip: f({ id: 'alarms.modal.rule.button.addrule.tooltip' }),
      submitCallback: handleSubmit(handleOnSubmit),
    },
  ];

  const getDataForOptions = async (value: string) => {
    setIsLoaderActive(true);
    setDataOptions(null);
    const data = await getData(value);
    setIsLoaderActive(false);
    if (data) {
      setDataOptions(data);
    }
  };

  const getRuleValidation = async () => {
    const data = await getValidationAll();
    if (data) {
      setValidation(data);
    }
  };

  const getSpecificValidation = async (selectedValue: string) => {
    setIsLoaderActive(true);
    const data = await getValidation(selectedValue);
    setIsLoaderActive(false);
    if (data) {
      setSpecificValidation(data);
    }
  };

  const getLabel = (labelIdentificator: string) => {
    setLabelInputs(getLabels(labelIdentificator));
  };

  React.useEffect(() => {
    setIsReset(false);
  }, []);

  const closeWarning = () => {
    setIsAlert(false);
  };

  const handleOnChangeSelect = async (selectedValue: string) => {
    setIsLoaderActive(true);
    setFieldName(selectedValue as TRuleForm);
    setValue('condition', selectedValue);
    trackEvent(['alarm', 'change', `Alarm rule condition ${selectedValue}`]);

    if (getLabels(selectedValue).type === 'range') {
      if (selectedValue.length > 0) {
        await getSpecificValidation(selectedValue);
        getLabel(selectedValue);
      }
    } else if (selectedValue.length > 0) {
      await getDataForOptions(selectedValue);
      getLabel(selectedValue);
      await getSpecificValidation(selectedValue);
    }
    setIsLoaderActive(false);
  };

  const nextForm = (value: boolean) => {
    setIsNextForm(value);
  };

  const results = useQueries({
    queries: [
      {
        queryKey: ['ruleFormRules', props.selectedChips],
        queryFn: () => getAlarmRules(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
      {
        queryKey: ['ruleFormValidation'],
        queryFn: () => getRuleValidation(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
    ],
  });
  const { isLoading: isLoadingRules } = results[0];
  const { isLoading: isLoadingValidation } = results[1];
  const condition = watch('condition') as keyof IRuleFormValues;
  const selectedValue = getValues()[condition];

  const isLoadingData = isLoadingRules || isLoadingValidation || isLoaderActive;

  if (isAlert) {
    return (
      <CmdAlert
        id="rule-form-alert"
        data={{
          title: f({ id: 'alarms.modal.rule.out.of.options.title' }),
          message: f({ id: 'alarms.modal.rule.out.of.options.title.message' }),
          type: 'warning',
        }}
        isAbsolute={true}
        onCloseCallback={closeWarning}
      />
    );
  }

  if (!isEmptyOptions) {
    return (
      <CmdModal
        id="rule-form-modal"
        title={f({ id: 'alarms.modal.rule.title' })}
        footerButtons={buttons}
        loader={isLoadingData}
        modalFooterCheckbox={f({ id: 'alarms.modal.rule.nextForm' })}
        modalFooterCheckboxTitle={f({ id: 'alarms.modal.rule.nextForm.tooltip' })}
        modalFooterCheckboxCallback={nextForm}
      >
        {Object.keys(conditionSelectOption).length > 0 && (
          <Controller
            name="condition"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <GroupSelectHookForm
                {...field}
                id="condition"
                label={f({ id: 'alarms.modal.rule.conditions.select.title' })}
                placeholder={f({
                  id: 'alarms.modal.rule.conditions.select.placeholder.choose',
                })}
                options={conditionSelectOption}
                onChangeCallback={handleOnChangeSelect}
                newStyle={true}
                searchPlaceholder={f({
                  id: 'alarms.modal.rule.waypoint.search.placeholder',
                })}
                tooltip={f({ id: 'alarms.modal.rule.conditions.select.tooltip' })}
                reload={isReset}
                control={control}
                required={true}
              />
            )}
          />
        )}

        {validation && (
          <>
            {watch('condition') && dataOptions && isWaypointCondition(watch('condition')) && (
              <Controller
                name={watch('condition') as TRuleForm}
                control={control}
                rules={{
                  required: errors[condition]?.message
                    ? { value: true, message: String(errors[condition]?.message) }
                    : undefined,
                }}
                render={({ field }) => (
                  <GroupChipSelectHookForm
                    {...field}
                    id="waypoint_pick"
                    label={f({ id: 'alarms.modal.rule.waypoint.pick' })}
                    placeholder={f({ id: 'form.choose' })}
                    searchPlaceholder={f({
                      id: 'alarms.modal.rule.waypoint.search.placeholder',
                    })}
                    selectButton={f({
                      id: 'alarms.modal.rule.waypoint.select.button',
                    })}
                    resetButton={f({
                      id: 'alarms.modal.rule.waypoint.reset.button',
                    })}
                    options={dataOptions as IGroupChipSelectOptions}
                    selectedValues={
                      selectedValue &&
                      typeof selectedValue === 'object' &&
                      !Array.isArray(selectedValue)
                        ? (selectedValue as Record<string, string[]>)
                        : undefined
                    }
                    required={true}
                    onChangeCallback={(_number, values) => {
                      if (values.length > 0) {
                        setValue(fieldName as TRuleForm, {
                          [f({ id: 'alarms.modal.rule.choice.select' })]: [...new Set([...values])],
                          [f({ id: 'alarms.modal.rule.choice.not.select' })]: [],
                        });
                        trigger(fieldName as TRuleForm);
                      } else {
                        setValue(fieldName as TRuleForm, {
                          [f({ id: 'alarms.modal.rule.choice.select' })]: [],
                          [f({ id: 'alarms.modal.rule.choice.not.select' })]: [],
                        });
                        trigger(fieldName as TRuleForm);
                      }
                      trackEvent(['alarm', 'change', `Alarm rule waypoint`]);
                    }}
                    isNewGroup={true}
                    itemsNumber={3}
                    control={control}
                  />
                )}
              />
            )}

            {fieldName.length > 0 &&
              labelInputs &&
              labelInputs.type === RULE_TYPE_BINARY &&
              watch('condition') &&
              dataOptions &&
              !isWaypointCondition(watch('condition')) &&
              !isCheckboxGroupCondition(watch('condition')) &&
              !isRangeCondition(watch('condition')) &&
              !isCheckboxCondition(watch('condition')) && (
                <Controller
                  name={fieldName as TRuleForm}
                  control={control}
                  render={({ field }) => (
                    <RadioInputHookForm
                      {...field}
                      id={fieldName}
                      key={fieldName}
                      label=""
                      control={control}
                      options={dataOptions as IRadioInputValue[]}
                      required={
                        validation
                          ? FormikValidatorService.isFieldRequired(validation.fieldName)
                          : false
                      }
                      onChangeCallback={(value) => {
                        setValue(fieldName as TRuleForm, value);
                        trigger(fieldName as TRuleForm);
                        trackEvent(['alarm', 'change', `Alarm rule binary ${fieldName}`]);
                      }}
                      tooltipByValue={
                        radioBtnTooltipsTranslationKeys[watch('condition') as RadioBtnTooltipsKeys]
                      }
                    />
                  )}
                />
              )}

            {fieldName.length > 0 &&
              labelInputs &&
              labelInputs.type === RULE_TYPE_BINARY &&
              watch('condition') &&
              dataOptions &&
              !isWaypointCondition(watch('condition')) &&
              !isCheckboxGroupCondition(watch('condition')) &&
              !isRangeCondition(watch('condition')) &&
              isCheckboxCondition(watch('condition')) && (
                <Controller
                  name={fieldName as TRuleForm}
                  control={control}
                  render={({ field }) => (
                    <CheckboxInputHookForm
                      {...field}
                      id={fieldName}
                      key={fieldName}
                      label=""
                      control={control}
                      options={dataOptions as ICheckboxGroupOption[]}
                      required={
                        validation
                          ? FormikValidatorService.isFieldRequired(validation.fieldName)
                          : false
                      }
                      onChangeCallback={(value) => {
                        if (value) {
                          if (Array.isArray(dataOptions) && dataOptions.length > 0) {
                            setValue(fieldName as TRuleForm, dataOptions[0].value);
                          }
                          trigger(fieldName as TRuleForm);
                        }

                        // setValue(fieldName as TRuleForm, value);
                        // trigger(fieldName as TRuleForm);
                      }}
                    />
                  )}
                />
              )}

            {watch('condition') &&
              fieldName.length > 0 &&
              dataOptions &&
              isCheckboxGroupCondition(watch('condition')) && (
                <Controller
                  name={fieldName as TRuleForm}
                  control={control}
                  render={({ field }) => (
                    <CheckboxGroupHookForm
                      {...field}
                      label={f({
                        id: 'alarm.modal.rule.doors.open',
                      })}
                      id={fieldName}
                      options={dataOptions as ICheckboxGroupOption[]}
                      required={
                        validation
                          ? FormikValidatorService.isFieldRequired(validation.fieldName)
                          : false
                      }
                      control={control}
                      onChangeCallback={(value) => {
                        setValue(fieldName as TRuleForm, value);
                        trigger(fieldName as TRuleForm);
                        trackEvent(['alarm', 'change', `Alarm rule checkbox group ${fieldName}`]);
                      }}
                    />
                  )}
                />
              )}

            {fieldName.length > 0 &&
              labelInputs &&
              labelInputs.type === 'range' &&
              watch('condition') &&
              isRangeCondition(watch('condition')) && (
                <>
                  <Controller
                    name={'min' as TRuleForm}
                    control={control}
                    render={({ field }) => (
                      <CmdInput
                        {...field}
                        label={f({
                          id: `${labelInputs.indexIdMin}`,
                        })}
                        id="min"
                        type="number"
                        placeholder={f({
                          id: `${labelInputs.placeholder}`,
                        })}
                        required={
                          specificValidation
                            ? FormikValidatorService.isFieldRequired(specificValidation.min)
                            : false
                        }
                        onChangeCallback={(value) => {
                          setValue('min' as TRuleForm, value);
                          trigger('min' as TRuleForm);
                          trackEvent(['alarm', 'change', `Alarm rule range min ${fieldName}`]);
                        }}
                        error={errors.min?.message}
                        touched={true}
                      />
                    )}
                  />
                  <Controller
                    name={'max' as TRuleForm}
                    control={control}
                    render={({ field }) => (
                      <CmdInput
                        {...field}
                        label={
                          labelInputs.indexIdMax
                            ? f({
                                id: `${labelInputs.indexIdMax}`,
                              })
                            : ''
                        }
                        id="max"
                        type="number"
                        placeholder={f({
                          id: `${labelInputs.placeholder}`,
                        })}
                        required={
                          specificValidation
                            ? FormikValidatorService.isFieldRequired(specificValidation.max)
                            : false
                        }
                        onChangeCallback={(value) => {
                          setValue('max' as TRuleForm, value);
                          trigger('max' as TRuleForm);
                          trackEvent(['alarm', 'change', `Alarm rule range max ${fieldName}`]);
                        }}
                        error={errors.max?.message}
                        touched={true}
                      />
                    )}
                  />
                </>
              )}
          </>
        )}
      </CmdModal>
    );
  }

  return null;
}
